import { useState, useCallback, useEffect } from "react";
import axios from "axios";
import { Plus } from "lucide-react";
import { Button } from "@ui";
import { ProfilePickerSheet } from "./profile-picker-sheet";
import { SelectedProfileCard } from "./selected-profile-card";
import { PendingInviteCard } from "./pending-invite-card";
import type { Profile, Invitee, ProfilePickerProps } from "./types";

type SelectedItem = { type: "profile"; data: Profile } | { type: "invite"; data: Invitee };

export function ProfilePicker({
  profileType,
  searchUrl,
  mode,
  name,
  initialValue,
  allowInvite,
  label,
  sourceType,
  sourceId,
}: ProfilePickerProps & { sourceType?: string; sourceId?: number | string }) {
  // Normalize to array
  const normalizeValue = (
    val: Profile | Profile[] | null | undefined,
  ): Profile[] => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    return [val];
  };

  const [selected, setSelected] = useState<SelectedItem[]>([]);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  // Load initial profiles from initialValue
  useEffect(() => {
    if (initialValue) {
      const profiles = normalizeValue(initialValue);
      setSelected(profiles.map((p) => ({ type: "profile" as const, data: p })));
    }
  }, [initialValue]);

  // Load pending invitees scoped to this source slot on mount.
  // Backend filters by (current_user, source_type, source_id, profile_type)
  // — see Api::InviteesController#pending.
  useEffect(() => {
    if (sourceType && sourceId) {
      fetchPendingInvitees();
    }
  }, [sourceType, sourceId, profileType]);

  const fetchPendingInvitees = async () => {
    try {
      const response = await axios.get<Invitee[]>("/api/invitees/pending", {
        params: { source_type: sourceType, source_id: sourceId, profile_type: profileType },
        headers: { Accept: "application/json" },
      });
      setSelected((prev) => [
        ...prev.filter((item) => item.type !== "invite"),
        ...response.data.map((i) => ({ type: "invite" as const, data: i })),
      ]);
    } catch (error) {
      console.error("Failed to load pending invitees:", error);
    }
  };

  const handleSelect = useCallback(
    (profile: Profile) => {
      if (mode === "single") {
        setSelected([{ type: "profile", data: profile }]);
        setIsSheetOpen(false);
      } else {
        if (!selected.find((item) => item.type === "profile" && item.data.id === profile.id)) {
          setSelected([...selected, { type: "profile", data: profile }]);
        }
      }
    },
    [mode, selected],
  );

  const handleRemove = useCallback((item: SelectedItem) => {
    setSelected(selected.filter((i) => i !== item));
  }, [selected]);

  const handleInviteSent = useCallback((invitee: Invitee) => {
    setSelected((prev) => [...prev, { type: "invite", data: invitee }]);
    setIsSheetOpen(false);
  }, []);

  const handleCancelInvite = useCallback(async (invitee: Invitee) => {
    try {
      await axios.delete(`/api/invitees/${invitee.id}`);
      setSelected((prev) => prev.filter((i) => i.type !== "invite" || i.data.id !== invitee.id));
    } catch (error) {
      console.error("Failed to cancel invitee:", error);
    }
  }, []);

  const handleResendInvite = useCallback(async (invitee: Invitee) => {
    try {
      await axios.post(`/api/invitees/${invitee.id}/resend`);
    } catch (error) {
      console.error("Failed to resend invitation:", error);
    }
  }, []);

  const canAdd = mode === "multiple" || selected.length === 0;

  return (
    <div className="space-y-2">
      {/* Selected items (profiles and pending invites) */}
      {selected.length > 0 ? (
        <div className="space-y-2">
          {selected.map((item) =>
            item.type === "profile" ? (
              <SelectedProfileCard
                key={`profile-${item.data.id}`}
                profile={item.data}
                onRemove={() => handleRemove(item)}
              />
            ) : (
              <PendingInviteCard
                key={`invite-${item.data.id}`}
                invite={item.data}
                onCancel={() => handleCancelInvite(item.data)}
                onResend={() => handleResendInvite(item.data)}
              />
            )
          )}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center">
          <p className="text-sm text-slate-500">Aucune sélection</p>
        </div>
      )}

      {/* Add button */}
      {canAdd && (
        <Button
          variant="secondary"
          size="sm"
          icon={Plus}
          onClick={() => setIsSheetOpen(true)}
        >
          Ajouter
        </Button>
      )}

      {/* Hidden inputs for form submission (only profiles, not invites) */}
      {selected
        .filter((item) => item.type === "profile")
        .map((item) => (
          <input
            key={`profile-${item.data.id}`}
            type="hidden"
            name={mode === "multiple" ? `${name}[]` : name}
            value={item.data.id}
          />
        ))}

      {/* Empty hidden input when nothing selected (for single mode) */}
      {mode === "single" && selected.filter((item) => item.type === "profile").length === 0 && (
        <input type="hidden" name={name} value="" />
      )}

      {/* Sheet */}
      <ProfilePickerSheet
        open={isSheetOpen}
        onClose={() => setIsSheetOpen(false)}
        searchUrl={searchUrl}
        profileType={profileType}
        onSelect={handleSelect}
        onInviteSent={handleInviteSent}
        selectedIds={selected
          .filter((item) => item.type === "profile")
          .map((item) => item.data.id)}
        allowInvite={allowInvite}
        sourceType={sourceType}
        sourceId={sourceId}
        label={label}
      />
    </div>
  );
}
